import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { getOwner } from '@ember/owner';
import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { or, eq } from 'ember-truth-helpers';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import {
  HdsPageHeader,
  HdsAlert,
  HdsReveal,
  HdsIcon,
  HdsFormSelectField,
  HdsFormTextInputField,
  HdsButtonSet,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  Flex,
  FlexGrid,
  WithErrors,
} from 'core/components';
import { boxMargin, boxPadding } from 'core/modifiers';
import { optionForSourceState } from 'core/helpers';
import {
  FieldViolation,
  FormValidationError,
} from 'core/utils/form-validation';
import networksConnectionSourceType from 'networks-common/helpers/networks-connection-source-type';
import { TYPE_NETWORK_DNS_FORWARDING_RULE_IP } from 'common/utils/cloud-resource-types';

import type { HTMLElementEvent } from 'common/utils/forms';
import type ApiService from 'api/services/api';
import type {
  HashicorpCloudResourcemanagerOrganization,
  HashicorpCloudResourcemanagerProject,
} from '@clients/cloud-resource-manager';
import type {
  HashicorpCloudNetwork20200907CreateDNSForwardingRuleRequest as CreateDNSForwardingRuleRequest,
  HashicorpCloudNetwork20200907CreateDNSForwardingRequest as CreateDNSForwardingRequest,
  HashicorpCloudNetwork20200907DNSForwarding as DNSForwarding,
  HashicorpCloudNetwork20200907Network as Network,
  HashicorpCloudNetwork20200907Peering as Peering,
  HashicorpCloudNetwork20200907TGWAttachment as TGWAttachment,
} from '@clients/cloud-network';
import type RouterService from '@ember/routing/router-service';
import type FlashMessagesService from 'ember-cli-flash/services/flash-messages';
import type { IntlService } from 'ember-intl';
import type OperationService from 'core/services/operation';
import type Route from '@ember/routing/route';
import type QuotaService from 'core/services/quota';

export interface PageHvnDnsForwardingCreateSignature {
  Args: {
    model: {
      network: Network;
      project: HashicorpCloudResourcemanagerProject;
      organization: HashicorpCloudResourcemanagerOrganization;
      dnsForwardings: DNSForwarding[];
      peerings: Peering[];
      tgwAttachments: TGWAttachment[];
    };
  };
}

interface PeeringOption extends Peering {
  optionString: string;
  connectionType: 'tgw-attachment' | 'hvn-peering';
}

interface TGWAttachmentOption extends TGWAttachment {
  optionString: string;
  connectionType: 'tgw-attachment' | 'hvn-peering';
}

export default class PageHvnDnsForwardingCreate extends Component<PageHvnDnsForwardingCreateSignature> {
  @service declare readonly api: ApiService;
  @service declare readonly router: RouterService;
  @service declare readonly flashMessages: FlashMessagesService;
  @service declare readonly intl: IntlService;
  @service declare readonly operation: OperationService;
  @service declare readonly quota: QuotaService;

  @tracked connectionType: string | undefined;
  @tracked connection: string | undefined;
  @tracked IPs: string[] = [];
  @tracked domain = '';
  @tracked validationError: FormValidationError | null = null;
  @tracked ipsFieldIsPristine = true;
  @tracked domainFieldIsPristine = true;
  @tracked connectionFieldIsPristine = true;
  @tracked isForwardPrivateDomainBannerVisible = true;

  get provider() {
    if (this.args.model.network?.providerNetworkData?.awsNetworkData) {
      return 'aws';
    }
    return 'azure';
  }

  get connections() {
    const { peerings, tgwAttachments } = this.args.model;
    let peeringOptions: PeeringOption[] = [];
    let tgwAttachmentOptions: TGWAttachmentOption[] = [];
    if (peerings) {
      peeringOptions = peerings.map((peering) => {
        return {
          ...peering,
          connectionType: 'hvn-peering',
          optionString: `${this.intl.t(
            'components.page.dns-forwarding-rules.create.fields.connection.peering'
          )} ${peering.id}`,
        };
      });
    }
    if (tgwAttachments) {
      tgwAttachmentOptions = tgwAttachments.map((attachment) => {
        return {
          ...attachment,
          connectionType: 'tgw-attachment',
          optionString: `${this.intl.t(
            'components.page.dns-forwarding-rules.create.fields.connection.tgw'
          )} ${attachment.id}`,
        };
      });
    }
    return [...peeringOptions, ...tgwAttachmentOptions];
  }

  get hasExistingDnsForwardings() {
    return Boolean(this.args.model.dnsForwardings.length);
  }

  get dnsForwarding() {
    return this.args.model.dnsForwardings[0] || {};
  }

  get connectionId() {
    if (this.hasExistingDnsForwardings) {
      return this.args.model.dnsForwardings[0]?.peeringId;
    }
  }

  get existingConnection() {
    const { peerings, tgwAttachments } = this.args.model;
    if (!this.hasExistingDnsForwardings) return;
    const connections =
      this.dnsForwarding.connectionType === 'tgw-attachment'
        ? tgwAttachments
        : peerings;
    return connections.find((item) => item.id === this.dnsForwarding.peeringId);
  }

  get maximumIps() {
    return this.quota.for(TYPE_NETWORK_DNS_FORWARDING_RULE_IP);
  }

  hideForwardPrivateDomainBanner = () => {
    this.isForwardPrivateDomainBannerVisible = false;
  };

  updateConnection = (value: string) => {
    const connectionOption = this.connections.filter(
      (item) => item.id === value
    )[0];
    this.connectionType = connectionOption?.connectionType;
    this.connection = connectionOption?.id;
    this.connectionFieldIsPristine = false;
    this.validateForm();
  };

  updateIPs = (event: HTMLElementEvent<HTMLInputElement>) => {
    this.IPs = event.target.value
      .split(',')
      .map((ip) => ip.trim())
      .filter((str) => str);
    this.ipsFieldIsPristine = false;
    this.validateForm();
  };

  updateDomain = (event: HTMLElementEvent<HTMLInputElement>) => {
    this.domain = event.target.value?.trim();
    this.domainFieldIsPristine = false;
    this.validateForm();
  };

  validateIPs() {
    const errors = [] as FieldViolation[];
    const ipRegex = /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/;
    const validIPs = this.IPs.filter((ip) => ip.match(ipRegex));
    if (!this.IPs.length) {
      errors.push(
        new FieldViolation(
          this.intl.t(
            'components.page.dns-forwarding-rules.create.errors.ips.blank'
          ),
          'ips'
        )
      );
    }
    if (validIPs.length !== this.IPs.length) {
      errors.push(
        new FieldViolation(
          this.intl.t(
            'components.page.dns-forwarding-rules.create.errors.ips.invalid'
          ),
          'ips'
        )
      );
    }
    if (this.IPs.length > this.maximumIps) {
      errors.push(
        new FieldViolation(
          this.intl.t(
            'components.page.dns-forwarding-rules.create.errors.ips.maximum',
            { num: this.maximumIps }
          ),
          'ips'
        )
      );
    }
    return errors;
  }

  validateDomain() {
    const errors = [] as FieldViolation[];
    if (!this.domain.length) {
      errors.push(
        new FieldViolation(
          this.intl.t(
            'components.page.dns-forwarding-rules.create.errors.domain.blank'
          ),
          'domain'
        )
      );
    }
    const domainSplit = this.domain.split('.');
    const validDomainParts = domainSplit.filter((domainPart: string, index) => {
      if (index === domainSplit.length - 1) {
        const tldReg = /[a-z]*/i;
        return tldReg.test(domainPart);
      } else {
        const domainReg = /^([a-z])([a-z0-9|-]*)([a-z0-9])$/i;
        return domainReg.test(domainPart);
      }
    });
    if (
      validDomainParts.length !== domainSplit.length ||
      domainSplit.length === 1
    ) {
      errors.push(
        new FieldViolation(
          this.intl.t(
            'components.page.dns-forwarding-rules.create.errors.domain.invalid'
          ),
          'domain'
        )
      );
    }
    return errors;
  }

  validateForm = () => {
    const errors = [] as FieldViolation[];
    if (!this.ipsFieldIsPristine) {
      const ipErrors = this.validateIPs();
      errors.push(...ipErrors);
    }

    if (!this.domainFieldIsPristine) {
      const domainErrors = this.validateDomain();
      errors.push(...domainErrors);
    }

    if (!this.connectionFieldIsPristine && !this.hasExistingDnsForwardings) {
      if (!this.connection) {
        errors.push(
          new FieldViolation(
            this.intl.t(
              'components.page.dns-forwarding-rules.create.errors.connection.blank'
            ),
            'connection'
          )
        );
      }
    }

    if (errors.length) {
      this.validationError = new FormValidationError('Validation error');
      this.validationError.details = errors;
    } else {
      this.validationError = null;
    }
  };

  refreshRoute = () => {
    const route = getOwner(this)!.lookup(
      `route:cloud.orgs.detail.projects.detail.hvns.detail`
    ) as Route;
    return route.refresh();
  };

  save = task(
    { drop: true },
    async (event: HTMLElementEvent<HTMLButtonElement>) => {
      event.preventDefault();
      const { organization, project, network, dnsForwardings } =
        this.args.model;
      const orgId = organization.id;
      const projectId = project.id;
      const networkId = network.id;

      this.domainFieldIsPristine = false;
      this.ipsFieldIsPristine = false;
      this.connectionFieldIsPristine = false;
      this.validateForm();

      if (!orgId || !projectId || !networkId || this.validationError) {
        return;
      }

      let operation;

      if (this.hasExistingDnsForwardings) {
        const dnsForwardingId = dnsForwardings[0]?.id as string;
        const body = {
          dnsForwardingRule: {
            hvn: network,
            dnsForwardingId,
            rule: {
              inboundEndpointIps: this.IPs,
              domainName: this.domain,
            },
          },
        } as CreateDNSForwardingRuleRequest;
        const resp = await this.api.network.createDNSForwardingRule(
          orgId,
          projectId,
          networkId,
          dnsForwardingId,
          body
        );
        operation = resp.operation;
      } else {
        const body = {
          dnsForwarding: {
            hvn: network,
            connectionType: this.connectionType,
            peeringId: this.connection,
            rule: {
              inboundEndpointIps: this.IPs,
              domainName: this.domain,
            },
          },
        } as CreateDNSForwardingRequest;
        const resp = await this.api.network.createDNSForwarding(
          orgId,
          projectId,
          networkId,
          body
        );
        operation = resp.operation;
      }
      this.operation.addOperation(operation);
      this.refreshRoute();
      this.router.transitionTo(
        'cloud.orgs.detail.projects.detail.hvns.detail.dns-forwarding-rules'
      );
      this.flashMessages.success(
        this.intl.t(
          'components.page.dns-forwarding-rules.create.success.title'
        ),
        {
          content: this.intl.t(
            'components.page.dns-forwarding-rules.create.success.body'
          ),
        }
      );
    }
  );

  <template>
    <WithErrors
      @error={{or this.save.last.error this.validationError}}
      as |FormError FieldError|
    >
      <CoreLayoutMain>
        <:header>
          <HdsPageHeader as |PH|>
            <PH.Title data-test-dns-forwarding-create-title>
              {{t
                "components.page.dns-forwarding-rules.create.custom-rules.title"
              }}
            </PH.Title>
            <PH.Breadcrumb>
              <BreadcrumbsContainer />
            </PH.Breadcrumb>
          </HdsPageHeader>
        </:header>
        <:alert>
          <FormError data-test-form-error />
        </:alert>
        <:body>
          {{#if this.isForwardPrivateDomainBannerVisible}}
            <HdsAlert
              @onDismiss={{this.hideForwardPrivateDomainBanner}}
              @type="inline"
              {{boxMargin "0 0 md"}}
              data-test-dns-forwarding-create-forward-domain-banner
              as |A|
            >
              <A.Title>
                {{t
                  "components.page.dns-forwarding-rules.create.forward-private-domain.title"
                }}
              </A.Title>
              <A.Description>
                {{t
                  "components.page.dns-forwarding-rules.create.forward-private-domain.description"
                  provider=this.provider
                }}
                <HdsReveal
                  @text={{t
                    "components.page.dns-forwarding-rules.create.forward-private-domain.subtitle"
                    provider=this.provider
                  }}
                  {{boxMargin "md 0 0 md"}}
                >
                  <ol {{boxPadding "0 0 0 md"}}>
                    <li>
                      {{t
                        "components.page.dns-forwarding-rules.create.forward-private-domain.prerequisites1"
                        provider=this.provider
                      }}
                    </li>
                    <li>
                      {{t
                        "components.page.dns-forwarding-rules.create.forward-private-domain.prerequisites2"
                        provider=this.provider
                      }}
                    </li>
                    <li>
                      {{t
                        "components.page.dns-forwarding-rules.create.forward-private-domain.prerequisites3"
                        provider=this.provider
                      }}
                    </li>
                    <li>
                      {{t
                        (concat
                          "components.page.dns-forwarding-rules.create.forward-private-domain.prerequisites4-"
                          this.provider
                        )
                      }}
                    </li>
                    <li>
                      {{t
                        (concat
                          "components.page.dns-forwarding-rules.create.forward-private-domain.prerequisites5-"
                          this.provider
                        )
                      }}
                    </li>
                  </ol>
                </HdsReveal>
              </A.Description>
            </HdsAlert>
          {{/if}}
          <FlexGrid as |G|>
            {{#if (eq this.connections.length 0)}}
              <G.Item @xs="12" {{boxMargin "0 0 lg"}}>
                <HdsAlert
                  @color="warning"
                  @type="inline"
                  data-test-dns-forwarding-no-connections
                  as |A|
                >
                  <A.Title>
                    {{t
                      "components.page.dns-forwarding-rules.create.no-connections.title"
                    }}
                  </A.Title>
                  <A.Description>
                    {{t
                      "components.page.dns-forwarding-rules.create.no-connections.description"
                    }}
                  </A.Description>
                  <Flex @direction="column">
                    <A.LinkStandalone
                      @color="secondary"
                      @href={{t
                        "components.page.dns-forwarding-rules.create.no-connections.set-up-peering.url"
                        provider=this.provider
                      }}
                      @icon="external-link"
                      @text={{t
                        "components.page.dns-forwarding-rules.create.no-connections.set-up-peering.text"
                      }}
                    />
                    <A.LinkStandalone
                      @color="secondary"
                      @href={{t
                        "components.page.dns-forwarding-rules.create.no-connections.set-up-gateway.url"
                        provider=this.provider
                      }}
                      @icon="external-link"
                      @text={{t
                        "components.page.dns-forwarding-rules.create.no-connections.set-up-gateway.text"
                      }}
                      {{boxMargin "0 0"}}
                    />
                  </Flex>
                </HdsAlert>
              </G.Item>
            {{/if}}
            <G.Item @xs="12" @sm="12" @md="8" @lg="5">
              <form
                {{on "submit" this.save.perform}}
                class="hcp-hds-form"
                novalidate
                data-test-dns-forwarding-create-form
              >
                {{#if this.hasExistingDnsForwardings}}
                  <div {{boxPadding "xs 0"}} data-test-connection-info>
                    <p class="hds-typography-body-100 hds-foreground-faint">
                      {{t
                        "components.page.dns-forwarding-rules.create.connection-name"
                        isTGW=(eq
                          this.dnsForwarding.connectionType "tgw-attachment"
                        )
                      }}
                    </p>
                    <Flex @alignItems="center" @gap="2xs">
                      <HdsIcon @name="hashicorp" @isInline={{true}} />
                      <span class="hds-font-weight-semibold">
                        {{this.connectionId}}
                      </span>
                      {{#let
                        (optionForSourceState
                          (networksConnectionSourceType this.existingConnection)
                          this.existingConnection.state
                        )
                        as |option|
                      }}
                        <HdsIcon
                          @color={{option.color}}
                          @name={{option.icon}}
                          {{boxMargin "0 0 0 xs"}}
                        />
                        <span
                          class="hds-typography-body-100 hds-foreground-faint"
                        >
                          {{t option.text}}
                        </span>
                      {{/let}}
                    </Flex>
                    <hr />
                  </div>
                {{else}}
                  <div {{boxPadding "xs 0"}}>
                    <FieldError
                      @field="connection"
                      @hdsDelegatedError={{true}}
                      {{boxMargin "0 0 md"}}
                      as |err|
                    >
                      <HdsFormSelectField
                        @id="connection"
                        @isRequired={{true}}
                        {{on
                          "change"
                          (pick "target.value" this.updateConnection)
                        }}
                        data-test-dns-forwarding-connections
                        name="connection"
                        as |F|
                      >
                        <F.Label @controlId="connection">
                          {{t
                            "components.page.dns-forwarding-rules.create.fields.connection.label"
                          }}
                        </F.Label>
                        <F.HelperText @controlId="connection">
                          {{t
                            "components.page.dns-forwarding-rules.create.fields.connection.description"
                          }}
                        </F.HelperText>
                        <F.Options>
                          <option value disabled selected>
                            {{t
                              "components.page.dns-forwarding-rules.create.fields.connection.select"
                            }}
                          </option>
                          {{#if this.connections.length}}
                            {{#each this.connections as |connection|}}
                              <option
                                value={{connection.id}}
                              >{{connection.optionString}}</option>
                            {{/each}}
                          {{/if}}
                        </F.Options>
                        {{#if err}}
                          <F.Error data-test-dns-forwarding-connections-error>
                            {{err.description}}
                          </F.Error>
                        {{/if}}
                      </HdsFormSelectField>
                    </FieldError>
                  </div>
                {{/if}}
                <div {{boxPadding "xs 0"}}>
                  <FieldError
                    @field="domain"
                    @hdsDelegatedError={{true}}
                    {{boxMargin "0 0 md"}}
                    as |err|
                  >
                    <HdsFormTextInputField
                      @id="domain"
                      @isRequired={{true}}
                      {{on "change" this.updateDomain}}
                      data-test-dns-forwarding-domain
                      name="domain"
                      placeholder={{t
                        "components.page.dns-forwarding-rules.create.fields.domain.placeholder"
                      }}
                      as |F|
                    >
                      {{#if err}}
                        <F.Error data-test-dns-forwarding-domain-error>
                          {{err.description}}
                        </F.Error>
                      {{/if}}
                      <F.Label @controlId="domain">
                        {{t
                          "components.page.dns-forwarding-rules.create.fields.domain.label"
                        }}
                      </F.Label>
                      <F.HelperText @controlId="domain">
                        {{t
                          "components.page.dns-forwarding-rules.create.fields.domain.description"
                        }}
                      </F.HelperText>
                    </HdsFormTextInputField>
                  </FieldError>
                </div>
                <div {{boxPadding "xs 0"}}>
                  <FieldError
                    @field="ips"
                    @hdsDelegatedError={{true}}
                    {{boxMargin "0 0 md"}}
                    as |err|
                  >
                    <HdsFormTextInputField
                      @id="ips"
                      @isRequired={{true}}
                      data-test-dns-forwarding-ips
                      {{on "change" this.updateIPs}}
                      name="ips"
                      placeholder={{t
                        "components.page.dns-forwarding-rules.create.fields.ips.placeholder"
                      }}
                      as |F|
                    >
                      {{#if err}}
                        <F.Error data-test-dns-forwarding-ips-error>
                          {{err.description}}
                        </F.Error>
                      {{/if}}
                      <F.Label @controlId="ips">
                        {{t
                          "components.page.dns-forwarding-rules.create.fields.ips.label"
                        }}
                      </F.Label>
                      <F.HelperText @controlId="ips">
                        {{t
                          "components.page.dns-forwarding-rules.create.fields.ips.description"
                          num=this.maximumIps
                        }}
                      </F.HelperText>
                    </HdsFormTextInputField>
                  </FieldError>
                </div>
                <HdsButtonSet {{boxPadding "md 0"}}>
                  <HdsButton
                    type="submit"
                    data-test-submit
                    disabled={{this.save.isRunning}}
                    @text={{t
                      "components.page.dns-forwarding-rules.create.save"
                    }}
                  />
                  <HdsButton
                    @text={{t
                      "components.page.dns-forwarding-rules.create.cancel"
                    }}
                    @color="secondary"
                    @route="cloud.orgs.detail.projects.detail.hvns.detail.dns-forwarding-rules"
                  />
                </HdsButtonSet>
              </form>
            </G.Item>
          </FlexGrid>
        </:body>
      </CoreLayoutMain>
    </WithErrors>
  </template>
}
